import { Tooltip } from 'antd';
import { get, set } from 'lodash-es';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRouteMatch } from 'react-router';

import { useFormContext } from '../../../components/forms/forms';
import { FormItemSchemaDefinedSelect } from '../../../components/forms/selects';
import { SavedShipmentContext } from '../newShipment/SavedShipmentProvider';
import { SAVED_SHIPMENT_SERVICE_TYPE } from '../newShipment/savedShipmentsUtils';
import routes from '../../../app/routes';
import { mergeFormNames } from '../../../components/forms/formHelpers';

function ServiceTypeWrapper({ forceTbdServiceType, children }) {
  if (!forceTbdServiceType) {
    return children;
  }
  return (
    <Tooltip title={<FormattedMessage id="book.newShipment.serviceType.tbd" />}>
      <div>{children}</div>
    </Tooltip>
  );
}

const tbdOption = {
  value: {
    code: SAVED_SHIPMENT_SERVICE_TYPE,
    text: 'TBD',
  },
  label: 'TBD',
};

export function ServiceTypeField({ namePrefix }) {
  // This will be extracted from the available options (we know the code but not the text)
  const serviceTypeSavedShipment = useRef(tbdOption);

  const isAirRetrieval = !!useRouteMatch(routes.book.newAirRetrieval);
  const { enabled: savedShipmentEnabled, forceTbdServiceType } =
    useContext(SavedShipmentContext);
  const { values, formInstance, forceUpdate } = useFormContext();
  const currentValue = namePrefix
    ? get(values, [namePrefix, 'serviceType'])
    : values.serviceType;

  useEffect(() => {
    // It has to be set to this in "saved shipment" mode, so we force it
    if (
      forceTbdServiceType &&
      currentValue?.code !== SAVED_SHIPMENT_SERVICE_TYPE
    ) {
      formInstance.setFieldsValue({
        serviceType: serviceTypeSavedShipment.current.value,
      });
      forceUpdate();
    }

    // If saved shipment is not enabled, this option will be unset, since it's not available
    if (
      !savedShipmentEnabled &&
      currentValue?.code === SAVED_SHIPMENT_SERVICE_TYPE
    ) {
      formInstance.setFieldsValue({
        serviceType: null,
      });
      forceUpdate();
    }
  }, [
    currentValue,
    formInstance,
    forceTbdServiceType,
    forceUpdate,
    savedShipmentEnabled,
  ]);

  const mapOptions = useCallback(
    options => {
      // Remove "TBD" type if saved shipment mode is not enabled
      const filtered = savedShipmentEnabled
        ? options
        : options.filter(o => o.value?.code !== SAVED_SHIPMENT_SERVICE_TYPE);

      // If TBD service type is forced but not in the available options, add it
      if (
        forceTbdServiceType &&
        !filtered.some(o => o.value?.code === SAVED_SHIPMENT_SERVICE_TYPE)
      ) {
        const tbd = serviceTypeSavedShipment.current?.value
          ? serviceTypeSavedShipment.current
          : tbdOption;
        return [...filtered, tbd];
      }

      return filtered;
    },
    [savedShipmentEnabled, forceTbdServiceType, serviceTypeSavedShipment]
  );

  const withOptions = useCallback(
    options => {
      const tbdFromSchema = options.find(
        o => o.value?.code === SAVED_SHIPMENT_SERVICE_TYPE
      );

      // Resolve the TBD option: prefer the schema option, fall back to our constant
      if (tbdFromSchema) {
        serviceTypeSavedShipment.current = tbdFromSchema;
      } else if (forceTbdServiceType) {
        serviceTypeSavedShipment.current = tbdOption;
      } else {
        serviceTypeSavedShipment.current = { value: null };
      }

      const resolved = serviceTypeSavedShipment.current.value;
      const needsUpdate =
        currentValue?.code === SAVED_SHIPMENT_SERVICE_TYPE &&
        !currentValue?.text;
      const shouldForceValue =
        forceTbdServiceType &&
        currentValue?.code !== SAVED_SHIPMENT_SERVICE_TYPE;

      // Update the form value when TBD text is missing or when TBD needs to be forced
      if (needsUpdate || shouldForceValue) {
        if (namePrefix) {
          const newValue = {};
          set(newValue, [namePrefix, 'serviceType'], resolved);
          formInstance.setFieldsValue(newValue);
        } else {
          formInstance.setFieldsValue({ serviceType: resolved });
        }
        forceUpdate();
      }
    },
    [currentValue, forceUpdate, forceTbdServiceType, formInstance, namePrefix]
  );

  return (
    <ServiceTypeWrapper forceTbdServiceType={forceTbdServiceType}>
      <FormItemSchemaDefinedSelect
        name={mergeFormNames(namePrefix, 'serviceType')}
        schemaName="serviceInformation.serviceType"
        labelId="book.newShipment.label.serviceType"
        mapOptions={mapOptions}
        withOptions={withOptions}
        disabled={forceTbdServiceType || isAirRetrieval}
        preloadOptions
        {...(forceTbdServiceType && { rules: () => [] })}
      />
    </ServiceTypeWrapper>
  );
}
